import type { ParametrosElegibilidade, TipoElegibilidade } from './elegibilidade'

export type ProdutoCategoria =
  | 'VENDOR_FINANCE'
  | 'RISCO_SACADO'
  | 'CAPITAL_GIRO'
  | 'HOME_EQUITY'
  | 'CESSAO_RECEBIVEIS'
  | 'CONSIGNADO_PRIVADO'
  | 'COMISSAO_INCENTIVO'
  | 'SACADO360'
  | 'CAPITAL_GIRO_CAPEX'
  | 'DUPLICATA_ESCRITURAL'
  | 'SACADOR360'
  /** P1 (AN-PP1 §3.2): categoria do produto `OTHER_NEED` — sem elegibilidade, checklist nem simulação. */
  | 'OUTRA_NECESSIDADE'

/**
 * Status normativo do catálogo (Políticas Vigentes por Produto 25/10/2026 §1; AN-PP1 §2).
 * Espelha `StatusProduto` do backend. Não substitui `ativo` (interruptor operacional):
 * invariante `RETIRADA ⇒ ativo = false`.
 */
export type StatusProduto = 'ATIVA' | 'ATIVA_COM_ANALISE' | 'SEM_POLITICA_SIMULACAO' | 'RETIRADA'

export type TipoParceiro = 'BANCO' | 'FUNDO' | 'FINTECH' | 'SECURITIZADORA'

/** Peça de material comercial. Link, não arquivo — as peças vivem fora da plataforma. */
export interface MaterialComercial {
  titulo: string
  url: string
  tipo?: string
}

export interface PerguntaFrequente {
  pergunta: string
  resposta: string
}

/** P2: `ProdutoResponse.elegibilidade` — tipo, versão e parâmetros da vigente. */
export interface ResumoElegibilidadeProduto extends ParametrosElegibilidade {
  tipo: TipoElegibilidade
  versao?: number | null
  vigenciaInicio?: string | null
}

// Espelha o ProdutoResponse do backend (originei-produtos). O endpoint
// devolve o DTO diretamente (sem envelope { data }) — ver produtoService.
export interface Produto {
  id: string
  /**
   * Código estável do catálogo (AN-PP1 §1; ver `constants/codigosProduto`). Decisões usam o
   * código, nunca o nome. Opcional só para conviver com o backend anterior à V13 de produtos.
   */
  codigo?: string
  /** Status normativo (AN-PP1 §2). Opcional só para conviver com o backend anterior à V13. */
  status?: StatusProduto
  nome: string
  categoria: ProdutoCategoria
  descricao: string
  descricaoDetalhada?: string
  /*
   * P6 (AN-PP6 §7, produtos V15 parte C): os demonstrativos (valor/prazo/taxa) e as colunas V9 de
   * elegibilidade (faturamento anual mínimo/máximo, LTV percentual) saíram do banco e do
   * `ProdutoResponse`. A regra de elegibilidade é só `elegibilidade` (política versionada).
   */
  /**
   * @deprecated P6 (AN-PP6 §7): coluna removida pela produtos V15 — o backend NUNCA mais manda este
   * campo (sempre `undefined`). Fica no tipo só porque `NovaOportunidadePage` (outro executor) ainda
   * o lê como fallback anterior à P2; remover junto com esse fallback. Nenhum código novo deve lê-lo.
   */
  ltvMaximoPercentual?: number | null
  /** P2 (AN-PP2 §2.3): resumo da política de elegibilidade vigente (aditivo; nulo no backend antigo). */
  elegibilidade?: ResumoElegibilidadeProduto | null
  criteriosElegibilidade: string[]
  documentacaoNecessaria: string[]
  // Apenas os IDs (UUID) dos parceiros vinculados — o backend não embute o objeto.
  parceiros: string[]

  // Biblioteca de produtos (PDF, "Materiais dos produtos"). Todos opcionais —
  // a tela omite a seção quando não há conteúdo.
  perfilCliente?: string | null
  beneficios?: string[]
  materiaisComerciais?: MaterialComercial[]
  videoUrl?: string | null
  faq?: PerguntaFrequente[]

  /**
   * Texto da regra de comissão do produto (Figma "Produtos e Materiais": coluna Comissão
   * é a REGRA, nunca número calculado). O `ProdutoResponse` ainda não traz o campo
   * (lacuna relatada, Onda 8 / A16) — a tela mostra "—" enquanto isso.
   */
  regraComissao?: string | null

  ativo: boolean
  createdAt: string
  updatedAt: string
}

// Espelha o ParceiroResponse do backend.
export interface Parceiro {
  id: string
  nome: string
  tipo: TipoParceiro
  cnpj: string
  contato?: string
  email: string
  telefone?: string
  produtosOferecidos: string[]
  ativo: boolean
  createdAt: string
}

export interface ProdutoRequest {
  /** Obrigatório no POST (422 `CODIGO_OBRIGATORIO`); imutável no PUT (422 `CODIGO_IMUTAVEL`). */
  codigo?: string
  /** Só no POST; `RETIRADA` → 422 `STATUS_INVALIDO`. A edição usa `PATCH /status`. */
  status?: StatusProduto
  nome: string
  categoria: ProdutoCategoria
  descricao?: string
  descricaoDetalhada?: string
  criteriosElegibilidade?: string[]
  documentacaoNecessaria?: string[]
  parceiros?: string[]
  perfilCliente?: string | null
  beneficios?: string[]
  materiaisComerciais?: MaterialComercial[]
  videoUrl?: string | null
  faq?: PerguntaFrequente[]
}

// ─── Elegibilidade por produto (Etapa 7 da jornada do Consultor) ───
//
// As perguntas são DADO cadastrado pelo ADMIN, não código: o PDF pede o motor mas nunca
// lista as perguntas, e elas mudam com a política comercial. Espelham
// `dto/PerguntaElegibilidadeResponse` — confira campo a campo ao mexer.

export type TipoRespostaElegibilidade = 'SIM_NAO' | 'NUMERO' | 'ESCOLHA' | 'TEXTO'

/**
 * Limite do texto livre (TEXTO, 09/09). O mesmo número vive em
 * `TipoRespostaElegibilidade.TAMANHO_MAXIMO_TEXTO` no backend, que devolve pendência acima dele.
 */
export const TAMANHO_MAXIMO_RESPOSTA_TEXTO = 200

export type OperadorElegibilidade =
  | 'IGUAL_A'
  | 'DIFERENTE_DE'
  | 'MAIOR_OU_IGUAL'
  | 'MENOR_OU_IGUAL'
  | 'ENTRE'
  | 'UM_DE'
  | 'NENHUM_DE'

export interface PerguntaElegibilidade {
  id: string
  produtoId: string
  ordem: number
  enunciado: string
  tipoResposta: TipoRespostaElegibilidade
  /** Já vem quebrada em lista — o backend guarda como texto, uma opção por linha. */
  opcoes: string[]
  operador: OperadorElegibilidade
  valorEsperado?: string
  valorMinimo?: number
  valorMaximo?: number
  mensagemRecusa: string
  /** `false` = apenas alerta. O padrão é bloquear, conforme resposta do time comercial. */
  bloqueia: boolean
  ativo: boolean
}

export interface PerguntaElegibilidadeRequest {
  ordem?: number
  enunciado: string
  tipoResposta: TipoRespostaElegibilidade
  /** Uma opção por linha (só para ESCOLHA). */
  opcoes?: string | null
  operador: OperadorElegibilidade
  valorEsperado?: string | null
  valorMinimo?: number | null
  valorMaximo?: number | null
  mensagemRecusa: string
  bloqueia?: boolean
  ativo?: boolean
}

/** O "com o motivo" que o PDF exige. `perguntaId` nulo = limite de valor/prazo do produto. */
export interface MotivoElegibilidade {
  perguntaId?: string
  enunciado: string
  mensagem: string
}

export interface ResultadoElegibilidade {
  produtoId: string
  elegivel: boolean
  bloqueios: MotivoElegibilidade[]
  alertas: MotivoElegibilidade[]
  /** Perguntas obrigatórias sem resposta — falta informação, ninguém foi recusado. */
  pendentes: MotivoElegibilidade[]
  /**
   * Respostas em texto livre (09/09): o motor não decide; o operador/admin avalia na
   * validação da reserva. Não derruba `elegivel`.
   */
  avaliacaoManual?: MotivoElegibilidade[]
}

// ─── Política de elegibilidade do parceiro ───
//
// "POLÍTICA DO PRODUTO (Repete para cada produto que o parceiro opera)" — segunda linha do
// PDF de cadastro de elegibilidade. A política é do PAR (parceiro, produto): o mesmo banco
// aceita ticket de R$ 500 mil em Home Equity e de R$ 5 milhões em Risco Sacado.
//
// Os ~60 campos são DADO, não código: vêm do catálogo semeado do PDF, e a seção específica
// do produto é resolvida por `produtoCategoria`.

export type TipoValorCriterio = 'BOOLEAN' | 'NUMERO' | 'TEXTO' | 'LISTA'

export interface CriterioParceiro {
  definicaoId: string
  chave: string
  /** Seção do PDF — a tela agrupa como o documento agrupa. */
  secao: string
  rotulo: string
  tipoValor: TipoValorCriterio
  /** Nulo = critério comum a todos os produtos. */
  produtoCategoria?: string | null
  valor?: string | null
  opcoes: string[]
}

export interface PoliticaParceiro {
  id: string
  parceiroId: string
  produtoId: string
  produtoNome?: string
  produtoCategoria?: string
  ativa: boolean
  /** Seção 8 — o que faz o parceiro rejeitar automaticamente. */
  criteriosEliminatorios?: string | null
  /** Seção 9 — campo livre. */
  observacoesComerciais?: string | null
  criterios: CriterioParceiro[]
  preenchidos: number
  total: number
}

export interface SalvarPoliticaParceiroRequest {
  valores: Record<string, string>
  criteriosEliminatorios?: string | null
  observacoesComerciais?: string | null
  ativa: boolean
}

// ─── Motor de compatibilidade parceiro × operação ───

/** Um critério violado, com os dois lados — conferível pelo operador. */
export interface MotivoIncompatibilidade {
  rotulo: string
  valorPolitica: string
  valorOperacao?: string | null
}

export interface ParceiroCompativel {
  parceiroId: string
  nome: string
  tipo?: TipoParceiro
  compativel: boolean
  /** Opera o produto mas ninguém cadastrou a política — compatível POR AUSÊNCIA. */
  semPolitica: boolean
  motivos: MotivoIncompatibilidade[]
  /** Critérios preenchidos que o motor não conseguiu comparar — conferir à mão. */
  naoAvaliados: string[]
  criteriosEliminatorios?: string | null
  observacoesComerciais?: string | null
  criteriosPreenchidos: number
}
